import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { combineLatest, filter, map, of, switchMap, tap } from 'rxjs';
import { ExamInstanceEditorComponent } from 'src/app/components/main/employees/rrhh/exam-instance-editor/exam-instance-editor.component';
import { ExamInstanceItemsEditorComponent } from 'src/app/components/main/employees/rrhh/exam-instance-items-editor/exam-instance-items-editor.component';
import { ExamInstanceEvaluationStatusEnumLabels } from 'src/app/enums/exam-instance-evaluation-status.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamInstance } from 'src/app/models/exam_instance.model';
import { ExamInstanceEvaluation } from 'src/app/models/exam_instance_evaluation.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';
import { UtilsService } from 'src/app/services/utils.service';

type ExamInstanceEvaluationRow = ExamInstanceEvaluation & {
  user_name_label: string;
  status_label: string;
  final_score_label: string;
};

@Component({
  selector: 'app-exam-instance-detail',
  templateUrl: './exam-instance-detail.component.html',
  styleUrl: './exam-instance-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    ExamInstanceEditorComponent,
    ExamInstanceItemsEditorComponent,
  ],
})
export class ExamInstanceDetailComponent {
  private readonly api_service = inject(ApiService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly utils_service = inject(UtilsService);

  private readonly refresh_counter = signal(0);
  readonly exporting_pdfs = signal(false);

  readonly exam_instance_id = toSignal(
    this.activated_route.paramMap.pipe(
      map(param_map => {
        const raw_id = param_map.get('id');
        const parsed_id = raw_id ? Number.parseInt(raw_id, 10) : 0;

        return Number.isNaN(parsed_id) ? 0 : parsed_id;
      })
    ),
    { initialValue: 0 }
  );

  readonly loading = signal(true);
  readonly evaluations_loading = signal(true);

  readonly exam_instance = toSignal(
    combineLatest([toObservable(this.exam_instance_id), toObservable(this.refresh_counter)]).pipe(
      switchMap(([exam_instance_id]) => {
        if (!exam_instance_id) {
          this.loading.set(false);
          return of(null);
        }

        this.loading.set(true);

        return this.api_service
          .getExamInstance(exam_instance_id, {
            with: 'exam_template,exam_instance_items',
          })
          .pipe(tap(() => this.loading.set(false)));
      })
    ),
    { initialValue: null as ExamInstance | null }
  );

  readonly evaluations_data_source = new MatTableDataSource<ExamInstanceEvaluationRow>([]);

  readonly evaluations_displayed_columns: string[] = ['user', 'status', 'final_score', 'created_at', 'actions'];

  readonly template_name_label = computed(() => this.exam_instance()?.exam_template?.name ?? '—');

  readonly has_evaluations = computed(() => {
    const evaluations_count = this.exam_instance()?.exam_instance_evaluations_count;

    if (evaluations_count !== undefined && evaluations_count !== null) {
      return evaluations_count > 0;
    }

    return this.evaluations_data_source.data.length > 0;
  });

  readonly can_edit_exam_structure = computed(() => !this.evaluations_loading() && !this.has_evaluations());

  readonly export_pdfs_disabled_reason = computed((): string => {
    if (this.evaluations_loading()) {
      return 'Cargando evaluaciones...';
    }

    if (!this.has_evaluations()) {
      return 'Crea al menos una evaluación antes de exportar los PDFs.';
    }

    if (this.exporting_pdfs()) {
      return 'Exportando PDFs...';
    }

    return '';
  });

  constructor() {
    combineLatest([toObservable(this.exam_instance_id), toObservable(this.refresh_counter)])
      .pipe(
        filter(([exam_instance_id]) => exam_instance_id > 0),
        tap(() => this.evaluations_loading.set(true)),
        switchMap(([exam_instance_id]) =>
          this.api_service.getExamInstanceEvaluations({
            exam_instance_id,
            per_page: 200,
            order_by: 'id:desc',
            with: 'user',
          })
        ),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: (response: ArrayResponse<ExamInstanceEvaluation> | ExamInstanceEvaluation[]): void => {
          const evaluations = Array.isArray(response) ? response : response.data;
          this.evaluations_data_source.data = evaluations.map(evaluation => this.mapEvaluationRow(evaluation));
          this.evaluations_loading.set(false);
        },
        error: (): void => {
          this.evaluations_loading.set(false);
          this.snack_bar.open('No se pudieron cargar las evaluaciones.', undefined, { duration: 5000 });
        },
      });
  }

  onExamInstanceSaved(): void {
    this.refresh_counter.update(counter => counter + 1);
  }

  exportAllEvaluationPdfs(): void {
    const exam_instance_id = this.exam_instance_id();

    if (!exam_instance_id || this.exporting_pdfs()) {
      return;
    }

    this.exporting_pdfs.set(true);

    this.api_service
      .exportExamInstanceEvaluationsPdf(exam_instance_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response): void => {
          this.exporting_pdfs.set(false);
          const filename =
            response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') ?? `examenes_${exam_instance_id}.zip`;

          this.utils_service.downloadDocumentFromBlob(response.body as Blob, filename);
        },
        error: async (error: unknown): Promise<void> => {
          this.exporting_pdfs.set(false);
          const message = (await this.utils_service.parseApiErrorFromBlob(error)) ?? 'No se pudieron exportar los PDFs.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  deleteEvaluation(exam_instance_evaluation: ExamInstanceEvaluation): void {
    const user_name = this.getUserDisplayName(exam_instance_evaluation.user);

    if (!confirm(`¿Eliminar la evaluación de ${user_name}?`)) {
      return;
    }

    this.api_service
      .deleteExamInstanceEvaluation(exam_instance_evaluation.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.evaluations_data_source.data = this.evaluations_data_source.data.filter(row => row.id !== exam_instance_evaluation.id);
          this.refresh_counter.update(counter => counter + 1);
          this.snack_bar.open('Evaluación eliminada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  private mapEvaluationRow(exam_instance_evaluation: ExamInstanceEvaluation): ExamInstanceEvaluationRow {
    return {
      ...exam_instance_evaluation,
      user_name_label: this.getUserDisplayName(exam_instance_evaluation.user),
      status_label: ExamInstanceEvaluationStatusEnumLabels[exam_instance_evaluation.status] ?? exam_instance_evaluation.status,
      final_score_label: exam_instance_evaluation.score_over_ten !== null ? `${exam_instance_evaluation.score_over_ten} / 10` : '—',
    };
  }

  private getUserDisplayName(user: User | undefined): string {
    if (!user) {
      return '—';
    }

    return `${user.name} ${user.surnames}`.trim();
  }
}
